<template>
  <div class="task-attachments">
    <div class="task-attachments__heading">
      {{ $t('approvals.ATTACHMENTS') }}
    </div>

    <div v-if="isAttachmentsEnabled" class="task-attachments__content">
      <div class="task-attachments__comment">
        <div class="task-attachments__label">{{ $t('approvals.COMMENT') }}</div>
        <p v-if="note" class="task-attachments__comment-text">
          {{ note }}
        </p>
        <p v-else class="task-attachments__empty-text">
          {{ $t('approvals.NO_COMMENT_PROVIDED') }}
        </p>
      </div>

      <div class="task-attachments__photo">
        <div class="task-attachments__label">{{ $t('approvals.PHOTO') }}</div>

        <button
          v-if="photo"
          :aria-label="$t('buttons.VIEW_IMAGE').toString()"
          :title="$t('buttons.VIEW_IMAGE').toString()"
          class="task-attachments__photo-button"
          @click="openPhotoModal"
        >
          <OptimizedImage
            :src="photo"
            class="task-attachments__photo-image"
            alt=""
            sizes="200px"
          />
        </button>
        <p v-else class="task-attachments__empty-text">
          {{ $t('approvals.NO_PHOTO_PROVIDED') }}
        </p>
      </div>
    </div>

    <div v-else class="task-attachments__content">
      <p class="task-attachments__empty-text">
        {{ $t('approvals.ATTACHMENTS_NOT_ENABLED_FOR_TASK') }}
      </p>
    </div>

    <PhotoModal
      :photo="photo"
      :is-open="isPhotoModalOpen"
      @close="closePhotoModal"
    />
  </div>
</template>

<script lang="ts" setup>
import PhotoModal from '@/components/generics/modals/PhotoModal.vue'
import OptimizedImage from '@/components/generics/OptimizedImage/OptimizedImage.vue'
import { useDialog } from '@/hooks/useDialog'
import { defineProps } from 'vue'

defineProps<{
  photo?: string
  note?: string
  isAttachmentsEnabled: boolean
}>()

const {
  isOpen: isPhotoModalOpen,
  openDialog: openPhotoModal,
  closeDialog: closePhotoModal,
} = useDialog()
</script>

<style lang="scss" scoped>
.task-attachments {
  @include cardStyle;
  display: flex;
  flex-direction: column;
  gap: $spacing-2xs;
  flex: 1 1 0;
  border: none;
  padding: $spacing-xs;
  background: $color-paper;

  &__heading {
    @include subtitle-2;
    font-weight: 700;
  }

  &__content {
    display: flex;
    flex: 1 1 0;
    gap: $spacing-m;
    flex-direction: column;
    padding: $spacing-3xs;
    overflow-y: auto;
  }

  &__label {
    @include formLabelStyle;
  }

  &__empty-text {
    color: $color-text-light;
    font-style: italic;
  }

  &__comment {
    display: flex;
    flex-direction: column;
    gap: $spacing-5xs;
  }

  &__comment-text {
    white-space: pre-wrap;
  }

  &__photo {
    display: flex;
    flex-direction: column;
    gap: $spacing-5xs;
  }

  &__photo-button {
    width: fit-content;
    border: none;
    background: none;
    cursor: zoom-in;
    transition: opacity 200ms ease-in-out;

    &:hover {
      opacity: 0.8;
    }
  }

  &__photo-image {
    @include productImageThumbnail();
    aspect-ratio: 1/1;
    border-radius: $border-radius-l;
    width: 200px;
    height: 200px;
  }

  @media (max-width: $medium-desktop-width-max) {
    &__attachments {
      flex-direction: column;
      justify-content: flex-start;
    }

    &__photo-image {
      width: 150px;
      height: 150px;
    }
  }
}
</style>
